'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { useSearchParams } from 'next/navigation';

import { IS_PRODUCTION } from '@/common/constants';
import { useDebugMode } from '@/components/providers/debug-mode-provider';

export const DEV_PROVIDERS = { vertex: 'vertex', gemini: 'gemini', openai: 'openai' } as const;
export const DEV_REASONING_LEVELS = ['none', 'minimal', 'low', 'medium', 'high'] as const;

export type DevProvider = (typeof DEV_PROVIDERS)[keyof typeof DEV_PROVIDERS];
export type DevReasoningLevel = (typeof DEV_REASONING_LEVELS)[number];

function defineModelOption<
  const M extends string,
  const P extends DevProvider,
  const B extends string,
>({ model, provider, baseKey }: { model: M; provider: P; baseKey: B }) {
  type TK = P extends typeof DEV_PROVIDERS.openai ? B : `${B}${Capitalize<P>}`;
  const suffix =
    provider === DEV_PROVIDERS.openai ? '' : provider.charAt(0).toUpperCase() + provider.slice(1);
  return {
    id: `${model}:${provider}` as `${M}:${P}`,
    model,
    provider,
    translationKey: `${baseKey}${suffix}` as TK,
  };
}

export const DEV_MODEL_OPTIONS = [
  defineModelOption({
    model: 'gemini-3-flash-preview',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini3Flash',
  }),
  defineModelOption({
    model: 'gemini-3.1-flash-lite',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini31FlashLite',
  }),
  defineModelOption({
    model: 'gemini-3.5-flash-lite',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini35FlashLite',
  }),
  defineModelOption({
    model: 'gemini-3.6-flash',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini36Flash',
  }),
  defineModelOption({
    model: 'gemini-3.7-flash',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini37Flash',
  }),
  defineModelOption({
    model: 'gemini-3.8-flash',
    provider: DEV_PROVIDERS.vertex,
    baseKey: 'gemini38Flash',
  }),
  defineModelOption({
    model: 'gemini-3-flash-preview',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini3Flash',
  }),
  defineModelOption({
    model: 'gemini-3.1-flash-lite',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini31FlashLite',
  }),
  defineModelOption({
    model: 'gemini-3.5-flash-lite',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini35FlashLite',
  }),
  defineModelOption({
    model: 'gemini-3.6-flash',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini36Flash',
  }),
  defineModelOption({
    model: 'gemini-3.7-flash',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini37Flash',
  }),
  defineModelOption({
    model: 'gemini-3.8-flash',
    provider: DEV_PROVIDERS.gemini,
    baseKey: 'gemini38Flash',
  }),
  defineModelOption({
    model: 'gpt-5.6-luna',
    provider: DEV_PROVIDERS.openai,
    baseKey: 'gpt56Luna',
  }),
  defineModelOption({
    model: 'gpt-6-luna',
    provider: DEV_PROVIDERS.openai,
    baseKey: 'gpt6Luna',
  }),
  defineModelOption({
    model: 'gpt-5.4-mini-2026-03-17',
    provider: DEV_PROVIDERS.openai,
    baseKey: 'gpt54Mini',
  }),
  defineModelOption({
    model: 'gpt-5.4-nano-2026-03-17',
    provider: DEV_PROVIDERS.openai,
    baseKey: 'gpt54Nano',
  }),
] as const;

export const DEV_MODELS = DEV_MODEL_OPTIONS.map(option => option.id);

export type DevModel = (typeof DEV_MODELS)[number];
export type DevModelName = (typeof DEV_MODEL_OPTIONS)[number]['model'];

export function getEffectiveReasoning({
  model,
  provider,
  reasoning,
}: {
  model: DevModelName;
  provider: DevProvider;
  reasoning: DevReasoningLevel;
}): DevReasoningLevel {
  return reasoning === 'minimal' && !supportsMinimalReasoning({ model, provider })
    ? 'low'
    : reasoning;
}

function supportsMinimalReasoning({
  model,
  provider,
}: {
  model: DevModelName;
  provider: DevProvider;
}): boolean {
  return provider !== DEV_PROVIDERS.openai && model !== 'gemini-3.7-flash';
}

export function getAvailableReasoningLevels({
  model,
  provider,
}: {
  model: DevModelName;
  provider: DevProvider;
}): readonly DevReasoningLevel[] {
  return supportsMinimalReasoning({ model, provider })
    ? DEV_REASONING_LEVELS
    : DEV_REASONING_LEVELS.filter(level => level !== 'minimal');
}

const MODEL_STORAGE_KEY = 'bazak-dev-model';
const REASONING_STORAGE_KEY = 'bazak-dev-reasoning';
const DEVTOOLS_URL_PARAM = 'devtools';

export const DEFAULT_MODEL: DevModel = 'gemini-3.7-flash:vertex';
export const DEFAULT_REASONING: DevReasoningLevel = 'medium';
export const DEV_MODEL_OPTIONS_BY_ID = Object.fromEntries(
  DEV_MODEL_OPTIONS.map(option => [option.id, option]),
) as Record<DevModel, (typeof DEV_MODEL_OPTIONS)[number]>;
const DEFAULT_MODEL_OPTION = DEV_MODEL_OPTIONS_BY_ID[DEFAULT_MODEL];

interface DevToolsContextType {
  showDevTools: boolean;
  model: DevModel;
  selectedModel: DevModelName;
  provider: DevProvider;
  reasoning: DevReasoningLevel;
  setModel: (model: DevModel) => void;
  setReasoning: (level: DevReasoningLevel) => void;
}

const DevToolsContext = createContext<DevToolsContextType | undefined>(undefined);

const NOOP_CONTEXT: DevToolsContextType = {
  showDevTools: false,
  model: DEFAULT_MODEL,
  selectedModel: DEFAULT_MODEL_OPTION.model,
  provider: DEFAULT_MODEL_OPTION.provider,
  reasoning: DEFAULT_REASONING,
  setModel: () => {},
  setReasoning: () => {},
};

function readStorage<T extends string>({
  key,
  fallback,
  allowed,
}: {
  key: string;
  fallback: T;
  allowed: readonly T[];
}): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored && (allowed as readonly string[]).includes(stored)) {
      return stored as T;
    }
  } catch {
    // private mode / sandboxed iframes
  }
  return fallback;
}

function writeStorage({ key, value }: { key: string; value: string }): void {
  try {
    if (localStorage.getItem(key) === value) return;
    localStorage.setItem(key, value);
    window.dispatchEvent(new StorageEvent('storage', { key }));
  } catch {
    // private mode / sandboxed iframes
  }
}

function subscribeToKey({ key, callback }: { key: string; callback: () => void }): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === key) callback();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

function useStorageValue<T extends string>({
  key,
  fallback,
  allowed,
}: {
  key: string;
  fallback: T;
  allowed: readonly T[];
}): [T, (value: T) => void] {
  const value = useSyncExternalStore(
    useCallback(cb => subscribeToKey({ key, callback: cb }), [key]),
    () => readStorage({ key, fallback, allowed }),
    () => fallback,
  );
  const setValue = useCallback((next: T) => writeStorage({ key, value: next }), [key]);
  return [value, setValue];
}

function ActiveDevToolsProvider({ children }: { children: ReactNode }) {
  const { isDebugMode } = useDebugMode();
  const [model, setModel] = useStorageValue({
    key: MODEL_STORAGE_KEY,
    fallback: DEFAULT_MODEL,
    allowed: DEV_MODELS,
  });
  const [reasoning, setReasoning] = useStorageValue({
    key: REASONING_STORAGE_KEY,
    fallback: DEFAULT_REASONING,
    allowed: DEV_REASONING_LEVELS,
  });

  const selectedModelOption = DEV_MODEL_OPTIONS_BY_ID[model] ?? DEFAULT_MODEL_OPTION;
  const { model: selectedModel, provider } = selectedModelOption;

  const value = useMemo(
    () => ({
      showDevTools: isDebugMode,
      model,
      selectedModel,
      provider,
      reasoning,
      setModel,
      setReasoning,
    }),
    [isDebugMode, model, selectedModel, provider, reasoning, setModel, setReasoning],
  );

  return <DevToolsContext.Provider value={value}>{children}</DevToolsContext.Provider>;
}

/**
 * Provider that exposes the dev-only model/reasoning selectors to the tree.
 *
 * Skips all heavy work (storage subscriptions, debug-mode lookup) when
 * `?devtools` is not in the URL — the param is required for the active branch
 * to mount, so production traffic pays no runtime cost. Uses `useSearchParams()`
 * which is consistent between SSR and CSR, so there is no hydration mismatch.
 */
export function DevToolsProvider({ children }: { children: ReactNode }) {
  const hasDevtoolsParam = useSearchParams().has(DEVTOOLS_URL_PARAM);

  if (!hasDevtoolsParam) {
    return <DevToolsContext.Provider value={NOOP_CONTEXT}>{children}</DevToolsContext.Provider>;
  }
  return <ActiveDevToolsProvider>{children}</ActiveDevToolsProvider>;
}

/**
 * Read the current dev-tools selection plus the `showDevTools` flag.
 *
 * `showDevTools` is true only when `?devtools` is in the URL AND debug mode
 * is on. When the flag is false, `model` and `reasoning` still return the
 * defaults but should not be sent to the backend.
 *
 * Throws in dev if called outside `<DevToolsProvider>`; falls back to a
 * no-op context in production to avoid breaking deployed apps.
 */
export function useDevTools(): DevToolsContextType {
  const context = useContext(DevToolsContext);
  if (!context) {
    if (!IS_PRODUCTION) {
      throw new Error('useDevTools() must be called within <DevToolsProvider>');
    }
    return NOOP_CONTEXT;
  }
  return context;
}
